import { useRef } from 'react';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input';

// Shared helpers for react-phone-number-input fields in international mode
// (users V2: Add Staff drawer, profile phone fields).

// "+<calling code>" for a country (e.g. "IN" -> "+91"), "" if unknown.
export const callingPrefixOf = (country) => {
    if (!country) return '';
    try {
        return `+${getCountryCallingCode(country)}`;
    } catch (e) {
        return '';
    }
};

let callingCodes = null;
const getCallingCodes = () => {
    if (!callingCodes) {
        callingCodes = new Set();
        getCountries().forEach((country) => {
            try {
                callingCodes.add(getCountryCallingCode(country));
            } catch (e) { /* unsupported country */ }
        });
    }
    return callingCodes;
};

// Splits an E.164-ish value ("+3549898877888") into its calling-code prefix
// ("+354") and the digits typed after it ("9898877888"). Calling codes are
// prefix-free, so the first match is the right one.
export const splitPhone = (value) => {
    const v = String(value || '');
    if (!v.startsWith('+')) return { prefix: '', digits: v.replace(/\D/g, '') };
    const allDigits = v.slice(1).replace(/\D/g, '');
    const codes = getCallingCodes();
    for (let len = 1; len <= 3 && len <= allDigits.length; len += 1) {
        const code = allDigits.slice(0, len);
        if (codes.has(code)) return { prefix: `+${code}`, digits: allDigits.slice(len) };
    }
    return { prefix: '', digits: allDigits };
};

// Only Indian numbers (+91, or legacy values stored without a "+") are
// format-validated; other countries just need a number entered.
export const isIndianPhone = (value) => {
    const v = String(value || '');
    return !v.startsWith('+') || v.startsWith('+91');
};

// With `countryCallingCodeEditable={false}`, pressing Backspace with the caret
// right after the "+91" (or Delete inside it) removes a digit of the locked
// code; the library then sees a value that no longer starts with "+91" and
// resets the whole field to just "+91", wiping the number. The formatter
// (input-format) handles Backspace/Delete itself even if the event is
// default-prevented — it only checks `event.keyCode` — so block the browser
// default and blank the key code so the formatter ignores the key. The caret
// doesn't move at all. (Fallback if keyCode can't be overwritten: move the
// caret where the key is a no-op and put it back.)
// Use as: <PhoneInput numberInputProps={{ onKeyDown: keepCallingCodeOnKeyDown }} />
export const keepCallingCodeOnKeyDown = (event) => {
    if (event.key !== 'Backspace' && event.key !== 'Delete') return;
    const input = event.target;
    if (!input || typeof input.selectionStart !== 'number') return;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    if (start !== end) return; // A selection is erased as selected.
    const text = String(input.value || '');
    const { prefix } = splitPhone(text.replace(/\s/g, ''));
    if (!prefix || !text.startsWith(prefix)) return;
    const prefixLen = prefix.length;
    // Backspace up to just after the separator space ("+91 |") would eat into
    // the code; Delete anywhere inside the code itself would too.
    const touchesPrefix = event.key === 'Backspace'
        ? start > 0 && start <= prefixLen + 1
        : start < prefixLen;
    if (!touchesPrefix) return;
    event.preventDefault();
    try {
        event.keyCode = 0;
    } catch (e) { /* read-only — use the caret fallback below */ }
    if (event.keyCode === 0) return;
    const restoreAt = Math.min(start, prefixLen);
    const noOpAt = event.key === 'Backspace' ? 0 : text.length;
    input.setSelectionRange(noOpAt, noOpAt);
    setTimeout(() => {
        try { input.setSelectionRange(restoreAt, restoreAt); } catch (e) { /* input gone */ }
    }, 0);
};

// In international mode the library resets the field to just the new "+code"
// when the country changes, dropping the typed number. React calls
// onCountryChange (from the library's componentDidUpdate) BEFORE the library's
// onChange with that bare "+code", so remember the digits here and put them
// back after the new code in the following onChange.
//   const { handleChange, handleCountryChange } = useKeepPhoneDigitsOnCountryChange(value, onChange);
//   <PhoneInput value={value} onChange={handleChange} onCountryChange={handleCountryChange} />
export const useKeepPhoneDigitsOnCountryChange = (value, onChange) => {
    const valueRef = useRef(value);
    valueRef.current = value;
    const pendingRef = useRef(null);

    const handleChange = (val) => {
        let next = val;
        const pending = pendingRef.current;
        if (pending) {
            pendingRef.current = null;
            if (next === pending.prefix) next = `${pending.prefix}${pending.digits}`;
        }
        valueRef.current = next;
        onChange(next);
    };

    const handleCountryChange = (newCountry) => {
        const newPrefix = callingPrefixOf(newCountry);
        if (!newPrefix) return;
        const { digits } = splitPhone(valueRef.current);
        if (!digits) return;
        pendingRef.current = { prefix: newPrefix, digits };
        // The matching onChange arrives in the same commit; drop the pending
        // restore if none does (e.g. the library's mount-time country report).
        setTimeout(() => {
            if (pendingRef.current && pendingRef.current.prefix === newPrefix) pendingRef.current = null;
        }, 0);
    };

    return { handleChange, handleCountryChange };
};
